import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, CheckCircle2, Clock, AlertTriangle, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import api, { errMsg } from '../lib/api';
import { gbp, setPageMeta, ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS } from '../lib/shop';

export default function OrderPage() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [tx, setTx] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setData(data);
      return data;
    } catch { setData(false); return null; }
  };

  useEffect(() => {
    setPageMeta('Order');
    load();
    const tick = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [orderId]);

  useEffect(() => {
    clearInterval(pollRef.current);
    const st = data?.payment?.status;
    if (data && ['awaiting_payment', 'pending', 'detected', 'confirming', 'manual_review'].includes(st)) {
      pollRef.current = setInterval(load, 10000);
    }
    return () => clearInterval(pollRef.current);
  }, [data?.payment?.status]);

  if (data === null) return <div className="max-w-3xl mx-auto px-4 py-20"><div className="h-64 bg-[#F4F4F0] animate-pulse" /></div>;
  if (data === false) return <div className="max-w-3xl mx-auto px-4 py-20 text-center font-display font-bold uppercase text-2xl">Order not found</div>;

  const { order, payment } = data;
  const isCrypto = payment?.payment_provider === 'crypto_direct';
  const isBank = payment?.payment_provider === 'bank_transfer';
  const paid = ['successful', 'confirmed'].includes(payment?.status);
  const address = payment?.provider_metadata?.receiving_address;
  const expiry = payment?.expires_at ? new Date(payment.expires_at).getTime() : null;
  const expired = expiry ? nowTs > expiry && ['awaiting_payment', 'pending'].includes(payment?.status) : false;
  const minsLeft = expiry ? Math.max(0, Math.floor((expiry - nowTs) / 60000)) : 0;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  const submitTx = async () => {
    setSubmitting(true);
    try {
      await api.post(`/payments/${payment.id}/submit-tx`, { transaction_hash: tx });
      toast.success('Transaction hash submitted — we will confirm your payment.');
      setTx('');
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="order-page">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Order {order.order_number}</p>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2">
        {paid ? 'Payment confirmed' : isBank ? 'Order received' : 'Complete your payment'}
      </h1>
      <div className="flex flex-wrap gap-2 mt-4 font-mono2 text-[10px] uppercase tracking-widest">
        <span data-testid="order-status" className="border hairline bg-white px-3 py-1.5">Order: {ORDER_STATUS_LABELS[order.status] || order.status}</span>
        {payment && <span data-testid="payment-status" className="border hairline bg-white px-3 py-1.5">Payment: {PAYMENT_STATUS_LABELS[payment.status] || payment.status}</span>}
      </div>

      {paid && (
        <div className="mt-8 bg-[#ECFDF5] border border-[#6EE7B7] p-6 flex gap-4" data-testid="order-paid-banner">
          <CheckCircle2 className="w-6 h-6 text-[#1F8A58] shrink-0" />
          <div>
            <p className="font-display font-bold uppercase text-xl">Thank you — your order is paid</p>
            <p className="text-sm text-[#575B60] mt-1">A confirmation has been emailed to {order.customer_email}. We will notify you when it dispatches.</p>
          </div>
        </div>
      )}

      {!paid && isCrypto && payment && (
        <div className="mt-8 border hairline bg-white p-6" data-testid="crypto-payment-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="font-display font-bold uppercase text-xl">{payment.crypto_asset} payment</p>
            {!expired && expiry && (
              <span className="flex items-center gap-1.5 font-mono2 text-[11px] uppercase tracking-widest text-[#D97706]">
                <Clock className="w-4 h-4" /> Expires in ~{minsLeft} min
              </span>
            )}
          </div>
          {expired ? (
            <div className="mt-4 bg-[#FFF7ED] border border-[#FDE68A] p-4 text-sm text-[#92400E]">
              This payment window has expired. Please place a new order or contact orders@vqhealth.co.uk quoting {order.order_number}.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-6 mt-5">
              <div className="border hairline p-3 bg-white w-fit" data-testid="crypto-qr">
                <QRCodeSVG value={address || ''} size={176} />
              </div>
              <div className="space-y-4">
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Network</p>
                  <p className="font-display font-bold uppercase text-lg" data-testid="crypto-network">{payment.crypto_network}</p>
                </div>
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Exact amount</p>
                  <p className="font-mono2 font-bold text-xl" data-testid="crypto-amount">{payment.expected_crypto_amount} {payment.crypto_asset?.split(' ')[0]}</p>
                </div>
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Receiving address</p>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="font-mono2 text-xs bg-[#F4F4F0] px-3 py-2 break-all flex-1" data-testid="crypto-address">{address}</code>
                    <button data-testid="crypto-copy-address" onClick={() => copy(address, 'Address')} className="border hairline p-2 hover:border-[#0B0F12]"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Order reference:</span>
                  <span className="font-mono2 text-xs font-semibold">{order.order_number}</span>
                </div>
                {(payment.crypto_asset || '').includes('USDT') && (
                  <p className="flex items-start gap-2 bg-[#FFF7ED] border border-[#FDE68A] p-3 text-[12px] text-[#92400E]">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    Only send USDT using the {payment.crypto_network} network. Transfers using an incorrect or unsupported network may not be recoverable.
                  </p>
                )}
                {payment.status === 'manual_review' ? (
                  <p className="text-sm text-[#575B60] border hairline p-3" data-testid="tx-submitted-note">
                    Transaction hash received. Your payment is being verified — status updates automatically.
                  </p>
                ) : (
                  <div>
                    <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mb-1.5">After sending, paste your transaction hash</p>
                    <div className="flex gap-2">
                      <input data-testid="tx-hash-input" value={tx} onChange={(e) => setTx(e.target.value)} placeholder="Transaction hash / TXID"
                        className="flex-1 border hairline px-3 py-2.5 text-sm font-mono2 focus:outline-none focus:border-[#0B0F12]" />
                      <button data-testid="tx-hash-submit" onClick={submitTx} disabled={submitting || tx.length < 8}
                        className="bg-[#0B0F12] text-white px-4 font-mono2 text-[10px] uppercase tracking-widest hover:bg-[#1F8A58] transition-colors disabled:opacity-50">
                        Submit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!paid && isBank && payment && (
        <div className="mt-8 border hairline bg-white p-6" data-testid="bank-transfer-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="font-display font-bold uppercase text-xl flex items-center gap-2"><Landmark className="w-5 h-5" /> Manual Bank Transfer</p>
            <span className="font-mono2 text-[10px] uppercase tracking-widest border border-[#FDE68A] bg-[#FFF7ED] text-[#92400E] px-3 py-1.5" data-testid="bank-status">Awaiting payment</span>
          </div>
          <p className="text-sm text-[#575B60] mt-3">Make a transfer for the exact amount below, quoting your order reference. These details have also been emailed to {order.customer_email}.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-5">
            <div className="bg-[#0B0F12] text-[#FAFAF8] p-5">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95]">Amount due</p>
              <p className="font-mono2 font-bold text-3xl mt-1" data-testid="bank-amount">{gbp(order.total)}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#8A8F95] mt-4">Payment reference</p>
              <div className="flex items-center gap-2 mt-1">
                <p className="font-mono2 font-bold text-xl" data-testid="bank-reference">{order.order_number}</p>
                <button data-testid="bank-copy-ref" onClick={() => copy(order.order_number, 'Reference')} className="border border-[#2A3138] p-1.5 hover:border-[#FAFAF8]"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              {payment.expires_at && (
                <p className="font-mono2 text-[10px] uppercase tracking-wider text-[#D97706] mt-4 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Please pay by {new Date(payment.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              )}
            </div>
            <div className="space-y-2.5">
              {[['Account name', payment.provider_metadata?.account_name, 'bank-account-name'],
                ['Sort code', payment.provider_metadata?.sort_code, 'bank-sort-code'],
                ['Account number', payment.provider_metadata?.account_number, 'bank-account-number'],
                ['IBAN', payment.provider_metadata?.iban, 'bank-iban'],
                ['SWIFT/BIC', payment.provider_metadata?.swift, 'bank-swift'],
                ['Bank', payment.provider_metadata?.bank_name, 'bank-name'],
              ].filter(([, v]) => v).map(([label, value, tid]) => (
                <div key={tid} className="flex items-center justify-between border-b hairline pb-2">
                  <span className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A8F95]">{label}</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono2 font-semibold text-sm" data-testid={tid}>{value}</span>
                    <button data-testid={`${tid}-copy`} onClick={() => copy(value, label)} className="text-[#8A8F95] hover:text-[#0B0F12]"><Copy className="w-3.5 h-3.5" /></button>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {payment.provider_metadata?.instructions && (
            <p className="mt-5 border hairline bg-[#FAFAF8] p-4 text-sm text-[#575B60]" data-testid="bank-instructions">{payment.provider_metadata.instructions}</p>
          )}
          <p className="mt-4 flex items-start gap-2 bg-[#FFF7ED] border border-[#FDE68A] p-4 text-[12px] text-[#92400E]" data-testid="bank-notice">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            Your order will be processed after payment has been received and confirmed. Use the exact payment reference above — transfers without it cannot be matched automatically.
          </p>
        </div>
      )}

      {!paid && payment?.payment_provider === 'stripe' && (
        <div className="mt-8 border hairline bg-white p-6" data-testid="stripe-payment-panel">
          <p className="font-display font-bold uppercase text-xl">Card payment via Stripe</p>
          {payment.status === 'pending' && payment.provider_metadata?.checkout_url ? (
            <>
              <p className="text-sm text-[#575B60] mt-2">Your payment was not completed. You can resume it securely via Stripe — the order is confirmed only after Stripe verifies the payment.</p>
              <a data-testid="stripe-resume" href={payment.provider_metadata.checkout_url}
                className="inline-block mt-4 bg-[#0B0F12] text-white px-6 py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors">
                Resume card payment — {gbp(order.total)}
              </a>
            </>
          ) : (
            <p className="text-sm text-[#575B60] mt-2">Payment status: {PAYMENT_STATUS_LABELS[payment.status]}. Contact orders@vqhealth.co.uk quoting {order.order_number} for assistance.</p>
          )}
        </div>
      )}

      {!paid && !isCrypto && !isBank && payment?.payment_provider !== 'stripe' && payment && (
        <div className="mt-8 border hairline bg-white p-6" data-testid="bank-payment-panel">
          <p className="font-display font-bold uppercase text-xl">Pay by Bank</p>
          {payment.status === 'pending' ? (
            <p className="text-sm text-[#575B60] mt-2">Follow your bank's approval flow to complete this payment. Your order is confirmed only after the provider verifies the payment — never from a browser redirect alone.</p>
          ) : (
            <p className="text-sm text-[#575B60] mt-2">Payment status: {PAYMENT_STATUS_LABELS[payment.status]}. Contact orders@vqhealth.co.uk quoting {order.order_number} for assistance.</p>
          )}
        </div>
      )}

      <div className="mt-8 border hairline bg-white p-6">
        <p className="font-display font-bold uppercase text-xl mb-4">Order items</p>
        <div className="space-y-3">
          {order.items.map((it, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <img src={it.image} alt="" className="w-10 h-10 object-cover bg-[#F4F4F0]" />
              <span className="flex-1 font-display font-bold uppercase">{it.name} <span className="text-[#8A8F95]">{it.strength}</span></span>
              <span className="font-mono2 text-xs text-[#8A8F95]">×{it.quantity}</span>
              <span className="font-mono2 text-sm">{gbp(it.price * it.quantity)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t hairline font-mono2 text-sm space-y-1.5">
          <div className="flex justify-between text-[#575B60]"><span>Subtotal</span><span>{gbp(order.subtotal)}</span></div>
          <div className="flex justify-between text-[#575B60]"><span>Shipping ({order.shipping_method || 'standard'})</span><span>{order.shipping_cost ? gbp(order.shipping_cost) : 'FREE'}</span></div>
          {order.discount_amount > 0 && <div className="flex justify-between text-[#1F8A58]"><span>Discount {order.discount_code}</span><span>−{gbp(order.discount_amount)}</span></div>}
          <div className="flex justify-between font-bold text-base pt-1"><span>Total</span><span data-testid="order-total">{gbp(order.total)}</span></div>
        </div>
      </div>

      <Link to="/products" className="inline-block mt-6 font-mono2 text-[11px] uppercase tracking-widest underline underline-offset-4">Continue browsing</Link>
    </div>
  );
}
